import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { Button, Card } from '@/ui/primitives'

export type ZiEditata = { zi?: number | null; data?: string | null; titlu?: string | null; text?: string | null }
type Editabil = {
  personalizat: boolean; titlu: string | null; intro: string | null
  zile: ZiEditata[]; note: string | null; sursa: string
  actualizat?: string | null; actualizat_de?: string | null
}

const CAMP = 'w-full px-3 py-2 rounded-[var(--radius-control)] border border-line bg-surface text-[14px] focus:border-accent outline-none'

/** Editorul programului. Doua feluri de a-l umple: se corecteaza cel automat, sau se
 *  lipeste cel primit de la touroperator si aplicatia il taie in zile. */
export function EditorProgram({ id, inchide }: { id: string; inchide: () => void }) {
  const qc = useQueryClient()
  const [titlu, setTitlu] = useState('')
  const [intro, setIntro] = useState('')
  const [note, setNote] = useState('')
  const [sursa, setSursa] = useState('editat')
  const [zile, setZile] = useState<ZiEditata[]>([])
  const [lipit, setLipit] = useState('')
  const [mesaj, setMesaj] = useState<string | null>(null)
  const [eroare, setEroare] = useState<string | null>(null)

  const { data, isPending } = useQuery({
    queryKey: ['program-editabil', id],
    queryFn: () => api.get<Editabil>(`/reservations/${id}/program-editabil`),
  })

  useEffect(() => {
    if (!data) return
    setTitlu(data.titlu ?? ''); setIntro(data.intro ?? ''); setNote(data.note ?? '')
    setSursa(data.sursa || 'editat'); setZile(data.zile ?? [])
  }, [data])

  const taie = useMutation({
    mutationFn: () => api.post<{ intro: string | null; zile: ZiEditata[]; gasite: number; avertisment: string | null }>(
      `/reservations/${id}/program-editabil/din-text`, { text: lipit }),
    onSuccess: (r) => {
      setEroare(r.avertisment)
      if (r.gasite > 0) {
        setZile(r.zile)
        if (r.intro && !intro) setIntro(r.intro)
        setSursa('touroperator')
        setLipit('')
        setMesaj(`Am taiat textul in ${r.gasite} zile. Verifica-le si salveaza.`)
      }
    },
  })

  const salveaza = useMutation({
    mutationFn: () => api.put(`/reservations/${id}/program-editabil`,
      { titlu, intro, note, sursa, zile }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['itinerar', id] })
      qc.invalidateQueries({ queryKey: ['program-editabil', id] })
      inchide()
    },
    onError: (e) => setEroare(e instanceof Error ? e.message : 'Nu s-a putut salva'),
  })

  const revino = useMutation({
    mutationFn: () => api.del(`/reservations/${id}/program-editabil`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['itinerar', id] })
      qc.invalidateQueries({ queryKey: ['program-editabil', id] })
      inchide()
    },
  })

  const schimba = (i: number, camp: 'titlu' | 'text', v: string) =>
    setZile((z) => z.map((x, j) => (j === i ? { ...x, [camp]: v } : x)))
  const sterge = (i: number) => setZile((z) => z.filter((_, j) => j !== i))
  const adauga = () => setZile((z) => [...z, { zi: z.length + 1, data: null, titlu: '', text: '' }])

  if (isPending) return <Card className="p-5"><p className="text-muted">Se incarca…</p></Card>

  return (
    <Card className="p-5 space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-[15px] font-medium">Editeaza programul</h2>
        <span className="text-[12.5px] text-muted">
          {data?.personalizat
            ? `personalizat${data.actualizat_de ? ` de ${data.actualizat_de}` : ''}`
            : 'pornit de la programul compus din rezervare'}
        </span>
      </div>

      <div className="rounded-[var(--radius-card)] bg-page p-4 space-y-2">
        <p className="text-[13.5px] font-medium">Programul e de la touroperator?</p>
        <p className="text-[12.5px] text-muted">
          Lipeste-l aici asa cum l-ai primit. Il taiem in zile dupa „Ziua 1", „ZIUA 2:", „Z3." —
          apoi il corectezi mai jos.
        </p>
        <textarea value={lipit} onChange={(e) => setLipit(e.target.value)} rows={5}
          placeholder={'ZIUA 1 (12.09) BUCURESTI - ROMA\nIntalnire la aeroport…\n\nZIUA 2: ROMA - VATICAN\n…'}
          className={CAMP} />
        <Button onClick={() => { setMesaj(null); setEroare(null); taie.mutate() }}
          disabled={!lipit.trim() || taie.isPending}>
          {taie.isPending ? 'Se taie…' : 'Taie in zile'}
        </Button>
        {mesaj && <p className="text-[13px] text-good">{mesaj}</p>}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className="text-[12.5px] text-muted mb-1 block" htmlFor="prog-titlu">Titlul programului</label>
          <input id="prog-titlu" value={titlu} onChange={(e) => setTitlu(e.target.value)}
            placeholder="ex.: Circuit Toscana, 8 zile" className={CAMP} />
        </div>
        <div className="sm:col-span-2">
          <label className="text-[12.5px] text-muted mb-1 block" htmlFor="prog-intro">Introducere</label>
          <textarea id="prog-intro" value={intro} onChange={(e) => setIntro(e.target.value)} rows={2}
            className={CAMP} />
        </div>
      </div>

      <div className="space-y-3">
        {zile.map((z, i) => (
          <div key={i} className="grid gap-2 sm:grid-cols-[110px_1fr] items-start">
            <div className="pt-2">
              <p className="text-[13px] font-medium">Ziua {z.zi ?? i + 1}</p>
              {z.data && (
                <p className="text-[12px] text-muted tnum">
                  {new Date(z.data).toLocaleDateString('ro-RO', { day: '2-digit', month: 'short' })}
                </p>
              )}
              <button onClick={() => sterge(i)} className="mt-1 text-[12px] text-muted hover:text-bad">
                sterge ziua
              </button>
            </div>
            <div className="space-y-2">
              <input value={z.titlu ?? ''} onChange={(e) => schimba(i, 'titlu', e.target.value)}
                placeholder="Titlul zilei (ex.: ROMA - VATICAN)" className={CAMP} />
              <textarea value={z.text ?? ''} onChange={(e) => schimba(i, 'text', e.target.value)}
                rows={3} placeholder="Ce se intampla in ziua asta" className={CAMP} />
            </div>
          </div>
        ))}
        <Button onClick={adauga}>Adauga o zi</Button>
      </div>

      <div>
        <label className="text-[12.5px] text-muted mb-1 block" htmlFor="prog-note">
          Nota de final (conditii, bagaje, ce nu e inclus)
        </label>
        <textarea id="prog-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} className={CAMP} />
      </div>

      <div className="flex flex-wrap items-center gap-3 pt-1 border-t border-line">
        <Button variant="primary" onClick={() => { setEroare(null); salveaza.mutate() }}
          disabled={salveaza.isPending}>
          {salveaza.isPending ? 'Se salveaza…' : 'Salveaza programul'}
        </Button>
        <Button variant="quiet" onClick={inchide}>Renunta</Button>
        {data?.personalizat && (
          <button
            onClick={() => { if (confirm('Stergi programul scris de tine? Ramane cel compus din rezervare.')) revino.mutate() }}
            className="ml-auto text-[13px] text-muted hover:text-bad">
            Revino la programul automat
          </button>
        )}
        {eroare && <span className="text-[13px] text-bad">{eroare}</span>}
      </div>
    </Card>
  )
}
